const COLORS_KEY = 'site_colors';
const IMAGES_KEY = 'site_images';

export interface SiteColors {
  primary: string;
  primaryDark: string;
  primaryLight: string;
}

export const DEFAULT_COLORS: SiteColors = {
  primary: '#e11d48',
  primaryDark: '#be123c',
  primaryLight: '#fb7185',
};

export interface ImageConfig {
  key: string;
  label: string;
  defaultUrl: string;
  width: number;
  height: number;
}

export const SITE_IMAGES: ImageConfig[] = [
  {
    key: 'hero-home',
    label: 'Home Hero Background',
    defaultUrl: 'https://public.readdy.ai/ai/img_res/056ea2c5ba1f499061122aa521b88390.jpg',
    width: 1920,
    height: 1080,
  },
  {
    key: 'hero-about',
    label: 'About Page Hero Background',
    defaultUrl: 'https://readdy.ai/api/search-image?query=modern%20tech%20company%20office%20interior%20with%20large%20glass%20windows%2C%20open%20collaborative%20workspace%2C%20dark%20minimal%20design%20with%20red%20accent%20lighting%2C%20professional%20corporate%20atmosphere%2C%20futuristic%20and%20sleek%20environment%2C%20architectural%20photography&width=1920&height=900&seq=about-hero001&orientation=landscape',
    width: 1920,
    height: 900,
  },
  {
    key: 'hero-services',
    label: 'Services Page Hero Background',
    defaultUrl: 'https://readdy.ai/api/search-image?query=dark%20abstract%20technology%20services%20background%20with%20flowing%20digital%20lines%2C%20circuit%20patterns%20and%20glowing%20nodes%20on%20very%20dark%20surface%2C%20professional%20tech%20company%20banner%2C%20subtle%20red%20accent%20lighting%2C%20minimalist%20futuristic&width=1920&height=600&seq=srv-hero01&orientation=landscape',
    width: 1920,
    height: 600,
  },
  {
    key: 'hero-join',
    label: 'Join Us Page Hero Background',
    defaultUrl: 'https://readdy.ai/api/search-image?query=diverse%20modern%20tech%20team%20in%20collaborative%20workspace%2C%20energetic%20young%20professionals%20working%20together%20on%20laptops%20and%20screens%2C%20open%20office%20with%20dark%20interior%20design%2C%20red%20accent%20lighting%2C%20team%20meeting%20and%20collaboration%20scene&width=1920&height=800&seq=join-hero01&orientation=landscape',
    width: 1920,
    height: 800,
  },
  {
    key: 'hero-contact',
    label: 'Contact Page Hero Background',
    defaultUrl: 'https://readdy.ai/api/search-image?query=abstract%20dark%20communication%20concept%20with%20digital%20connection%20lines%20converging%20to%20a%20glowing%20center%20point%2C%20deep%20dark%20background%20with%20subtle%20red%20accent%20illumination%2C%20modern%20tech%20contact%20concept%20art%2C%20minimalist%20dark%20aesthetic&width=1920&height=600&seq=contact-hero01&orientation=landscape',
    width: 1920,
    height: 600,
  },
  {
    key: 'hero-revenue',
    label: 'Revenue Sharing Hero Background',
    defaultUrl: 'https://readdy.ai/api/search-image?query=abstract%20financial%20growth%20visualization%20with%20dark%20background%2C%20glowing%20red%20and%20gold%20growth%20chart%20curves%20ascending%20upward%2C%20premium%20business%20success%20concept%2C%20sophisticated%20dark%20atmosphere%20with%20dynamic%20light%20trails%2C%20revenue%20and%20profit%20theme&width=1920&height=1000&seq=rev-hero01&orientation=landscape',
    width: 1920,
    height: 1000,
  },
];

function hexToRgbParts(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '225 29 72';
  return `${r} ${g} ${b}`;
}

export function getSiteColors(): SiteColors {
  try {
    const saved = JSON.parse(localStorage.getItem(COLORS_KEY) || '{}') as Partial<SiteColors>;
    return { ...DEFAULT_COLORS, ...saved };
  } catch {
    return { ...DEFAULT_COLORS };
  }
}

export function saveSiteColors(colors: SiteColors): void {
  localStorage.setItem(COLORS_KEY, JSON.stringify(colors));
  applyColorStyles(colors);
}

export function applyColorStyles(colors?: SiteColors): void {
  const c = colors || getSiteColors();
  let style = document.getElementById('site-color-overrides') as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = 'site-color-overrides';
    document.head.appendChild(style);
  }
  style.textContent = `:root {
    --brand-red: ${hexToRgbParts(c.primary)};
    --brand-red-dark: ${hexToRgbParts(c.primaryDark)};
    --brand-red-light: ${hexToRgbParts(c.primaryLight)};
  }`;
}

export function getSiteImages(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(IMAGES_KEY) || '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

export function saveSiteImages(images: Record<string, string>): void {
  localStorage.setItem(IMAGES_KEY, JSON.stringify(images));
}

export function getImageUrl(key: string, defaultUrl: string): string {
  try {
    const images = JSON.parse(localStorage.getItem(IMAGES_KEY) || '{}') as Record<string, string>;
    const override = images[key];
    return override && override.trim() ? override.trim() : defaultUrl;
  } catch {
    return defaultUrl;
  }
}
